import { useEffect, useState, useRef, useCallback } from "react";

const MIN_DISPLAY_MS = 1500;
const FADE_DURATION_MS = 300;

/**
 * Hook for smooth message transitions with minimum display time.
 * Used by TaskListLoadingState.
 */
export function useMessageTransition(
  message: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const [displayedMessage, setDisplayedMessage] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const lastChangeRef = useRef(0);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!message || !enabled) return;

    if (!displayedMessage) {
      setDisplayedMessage(message);
      lastChangeRef.current = Date.now();
      return;
    }

    if (message === displayedMessage) return;

    const elapsed = Date.now() - lastChangeRef.current;
    const delay = Math.max(0, MIN_DISPLAY_MS - elapsed);

    const doTransition = () => {
      setIsTransitioning(true);
      fadeTimerRef.current = setTimeout(() => {
        setDisplayedMessage(message);
        lastChangeRef.current = Date.now();
        setIsTransitioning(false);
      }, FADE_DURATION_MS);
    };

    if (delay <= 0) {
      doTransition();
      return () => clearTimeout(fadeTimerRef.current);
    }

    const timer = setTimeout(doTransition, delay);
    return () => {
      clearTimeout(timer);
      clearTimeout(fadeTimerRef.current);
    };
  }, [message, displayedMessage, enabled]);

  const reset = useCallback(() => {
    clearTimeout(fadeTimerRef.current);
    setDisplayedMessage("");
    setIsTransitioning(false);
    lastChangeRef.current = 0;
  }, []);

  return { displayedMessage, isTransitioning, reset } as const;
}
